import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import CommonSelect from "../../../../core/common/commonSelect";

import { gender } from "../../../../core/common/selectoption/selectoption";
import { getAreas, AreaOption } from "../../../../service/transportAreas";
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice";
import { selectCurrentSession, selectSessions } from "../../../../core/data/redux/slice/sessionsSlice";
import { Class, getAllClasses } from "../../../../service/classes";
import {
  newRegistration,
  updateRegistration,
} from "../../../../service/registration";
import {
  getAllGroups,
  TestGroupResponse,
} from "../../../../service/test-group";
import { all_routes } from "../../../router/all_routes";
import CameraModal from "../cameraModal";
import { DEFAULT_BRANCH_ID } from "../../../../constant";
import { selectUser } from "../../../../core/data/redux/slice/user";
import PhoneInput from "../../../../components/PhoneNumber";

export type StudentFormValues = {
  session: string;
  firstName: string;
  lastName: string;
  appliedClass: string;
  gender: string;
  dateOfBirth: string;
  bloodGroup: string;
  religion: string;
  category: string;
  email: string;
  motherTongue: string;
  studentPicture?: File;
  fatherPicture?: File;
  motherPicture?: File;
  fatherName: string;
  fatherCnic: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  referrer: string;
  otherReferrer: string;
  motherName: string;
  motherEmail: string;
  motherPhoneNumber: string;
  area: string;
  transferCertificate: string;
  lastResult: string;
  birthCertificate: string;
  prevSchoolName: string;
  prevSchoolAddress: string;
  branch: string;
  system: string;
  interviewDate: string;
  interviewTime: string;
  testGroup: string;
  nationality: string;
  prevClass: string;
  allergies: string;
  hasMedicalIssue: boolean;
  medications: string;
  hasSiblings: boolean;
  noOfSiblings: string;
};

const FORM_INITIAL_VALUES = {
  session: "",
  firstName: "",
  lastName: "",
  appliedClass: "",
  gender: "",
  dateOfBirth: "",
  bloodGroup: "",
  religion: "",
  category: "",
  email: "",
  motherTongue: "",
  studentPicture: null,
  fatherPicture: null,
  motherPicture: null,
  fatherName: "",
  fatherCnic: "",
  fatherEmail: "",
  fatherPhoneNumber: "",
  referrer: "",
  otherReferrer: "",
  motherName: "",
  motherEmail: "",
  motherPhoneNumber: "",
  area: "",
  transferCertificate: "",
  lastResult: "",
  birthCertificate: "",
  prevSchoolName: "",
  prevSchoolAddress: "",
  branch: DEFAULT_BRANCH_ID,
  system: "",
  interviewDate: "",
  interviewTime: "",
  testGroup: "",
  nationality: "",
  prevClass: "",
  allergies: "",
  hasMedicalIssue: false,
  medications: "",
  hasSiblings: false,
  noOfSiblings: "",
};

const FILE_FIELDS = [
  "studentPicture",
  "fatherPicture",
  "motherPicture",
];

// Optional text fields (e.g. fatherEmail, motherEmail) can be stored as `null`
// in the database on existing records. Yup.string() (without `.nullable()`)
// treats `null` as an invalid type — "fatherEmail cannot be null" — even
// though the field is optional. Normalize null -> "" before loading an
// existing record into the form so editing it doesn't trip that validator.
const nullsToEmptyStrings = <T extends Record<string, any>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj || {}).map(([k, v]) => [k, v === null ? "" : v])
  ) as T;

const AddNewRegistration = () => {
  // Already restricted to the current campus's Systems (Branch.systems).
  const campusSystems = useSelector(selectSystemsForCurrentBranch);
  const sessions = useSelector(selectSessions);
  const currentSession = useSelector(selectCurrentSession);
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();
  const [allClass, setAllClass] = useState<Class[]>([]);
  const [testGroups, setTestGroups] = useState<TestGroupResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [karachiAreas, setKarachiAreas] = useState<AreaOption[]>([]);
  const user = useSelector(selectUser);
  const [formData, setFormData] = useState({
    ...FORM_INITIAL_VALUES,
    system: campusSystems[0]?._id || "",
    session: currentSession?._id || "",
  });
  const [otherReferrer, setOtherReferrer] = useState(false);
  const [registrationType, setRegistrationType] = useState<string | null>();
  const [showCamera, setShowCamera] = useState(false);
  const [currentCameraField, setCurrentCameraField] = useState<string | null>(
    null
  );

  let isEdit = location.state && location.state._id;
  const validationSchema = Yup.object().shape({
    session: Yup.string(),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string(),
    appliedClass: Yup.string().required("Class is required"),
    gender: Yup.string().required("Gender is required"),
    dateOfBirth: Yup.date()
      .max(new Date(), "Date of birth cannot be in future")
      .required("Date of birth is required"),
    bloodGroup: Yup.string(),
    religion: Yup.string(),
    category: Yup.string(),
    email: Yup.string().email("Invalid email"),
    motherTongue: Yup.string(),
    system: Yup.string().required("System is required!"),
    studentPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", (file) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      })
      .test("fileType", "Unsupported file format", (file) => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true;
      }),
    fatherPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", (file) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      })
      .test("fileType", "Unsupported file format", (file) => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true;
      }),
    motherPicture: Yup.mixed<File>()
      .nullable()
      .test("fileSize", "File too large", (file) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      })
      .test("fileType", "Unsupported file format", (file) => {
        return file ? ["image/jpeg", "image/png"].includes(file.type) : true;
      }),
    fatherName: Yup.string().required("Father's name is required"),
    fatherCnic: Yup.string(),
    fatherEmail: Yup.string().email("Invalid email"),
    fatherPhoneNumber: Yup.string().required(
      "Father's phone number is required"
    ),
    referrer: Yup.string(),
    otherReferrer: Yup.string(),
    motherName: Yup.string(),
    motherEmail: Yup.string().email("Invalid email"),
    motherPhoneNumber: Yup.string(),
    area: Yup.string(),
    transferCertificate: Yup.string(),
    lastResult: Yup.string(),
    birthCertificate: Yup.string(),
    prevSchoolName: Yup.string(),
    prevSchoolAddress: Yup.string(),
    branch: Yup.string().required("Branch is required"),
    nationality: Yup.string(),
    hasMedicalIssue: Yup.boolean(),
    allergies: Yup.string(),
    medications: Yup.string(),
    hasSiblings: Yup.boolean(),
    noOfSiblings: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      ...formData,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { setSubmitting, setErrors, setStatus }) => {
      try {
        if (registrationType === "interview+test" && !values.testGroup) {
          toast.warning("Please add test group before registration");
          setSubmitting(false);
          return;
        }

        if (
          registrationType === "interview" &&
          (!values.interviewDate || !values.interviewTime)
        ) {
          toast.warning(
            "Please select both interview date and time before registration"
          );
          setSubmitting(false);
          return;
        }

        const phoneFields = [
          "fatherPhoneNumber",
          "motherPhoneNumber",
          "guardianPhoneNumber",
        ];
        const form = new FormData();

        const fieldsToInclude = [...Object.keys(values)].filter((key) => {
          if (key === "testGroup" && registrationType === "interview") {
            return false;
          }
          return true;
        });

        for (const key of fieldsToInclude) {
          if (FILE_FIELDS.includes(key)) continue;

          let value = values[key as keyof typeof values];

          if (value === undefined || value === null) continue;

          if (phoneFields.includes(key)) {
            const stringValue = String(value || "");

            if (!stringValue.trim()) continue;

            value = stringValue.startsWith("+92")
              ? stringValue === "+92"
                ? ""
                : stringValue
              : `+92${stringValue}`;
          }

          form.append(key, String(value));
        }

        FILE_FIELDS.forEach((item) => {
          const file = values[item as keyof typeof values];
          if (file) form.append(item, file as any);
        });

        if (isEdit) {
          await updateRegistration(location.state._id, form);
          toast.success("Student updated successfully!");
        } else {
          await newRegistration(form);
          toast.success("Student registered successfully!");
        }

        navigate(routes.registrationFormList);
      } catch (error: any) {
        if (error.response?.data) {
          const errorData = error.response.data;
          const fieldErrors: any = {};
          if (errorData.errors && typeof errorData.errors === "object") {
            Object.keys(errorData.errors).forEach((key) => {
              if (errorData.errors[key]) {
                let frontendFieldName = key;
                if (key === "phone") {
                  if (
                    formik.values.fatherPhoneNumber &&
                    formik.values.motherPhoneNumber &&
                    formik.values.fatherPhoneNumber ===
                      formik.values.motherPhoneNumber
                  ) {
                    frontendFieldName = "fatherPhoneNumber";
                    fieldErrors["fatherPhoneNumber"] = errorData.errors[key];
                    fieldErrors["motherPhoneNumber"] = errorData.errors[key];
                  } else {
                    frontendFieldName = "fatherPhoneNumber";
                    fieldErrors[frontendFieldName] = errorData.errors[key];
                  }
                } else if (key === "cnic") {
                  frontendFieldName = "fatherCnic";
                  fieldErrors[frontendFieldName] = errorData.errors[key];
                } else {
                  fieldErrors[frontendFieldName] = Array.isArray(
                    errorData.errors[key]
                  )
                    ? errorData.errors[key][0]
                    : errorData.errors[key];
                }
              }
            });
          }

          const possibleDuplicateFields = [
            "email",
            "fatherCnic",
            "motherCnic",
            "guardianCnic",
            "fatherPhoneNumber",
            "motherPhoneNumber",
            "guardianPhoneNumber",
            "bFormNo",
            "phone",
            "cnic",
          ];

          possibleDuplicateFields.forEach((field) => {
            if (errorData[field] && typeof errorData[field] === "string") {
              let frontendFieldName = field;
              if (field === "phone") {
                fieldErrors["fatherPhoneNumber"] = errorData[field];
                fieldErrors["motherPhoneNumber"] = errorData[field];
              } else if (field === "cnic") {
                frontendFieldName = "fatherCnic";
                fieldErrors[frontendFieldName] = errorData[field];
              } else {
                fieldErrors[frontendFieldName] = errorData[field];
              }
            }
          });

          Object.keys(errorData).forEach((key) => {
            if (
              typeof errorData[key] === "string" &&
              !["message", "success", "status"].includes(key) &&
              !fieldErrors[key]
            ) {
              fieldErrors[key] = errorData[key];
            }
          });

          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            toast.error("Please fix the validation errors");
          } else if (errorData.message) {
            toast.error(errorData.message);
          } else {
            toast.error("An error occurred while submitting the form");
          }
        } else {
          toast.error("Network error occurred");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchClasses = async (system: string) => {
    setLoading(true);
    try {
      const response = await getAllClasses({ system });
      setAllClass(response.classes);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchTestGroups = async (classId: string) => {
    try {
      const response = await getAllGroups({
        class: classId,
        session: formik.values.session,
      });

      setTestGroups(response.data);
    } catch (error) {
      console.error("Error fetching test groups:", error);
    }
  };
  const handleOptionChange = (e: any) => {
    formik.handleChange(e);
    if (e.target.value !== "Other") {
      formik.setFieldValue("otherReferrer", "");
      setOtherReferrer(false);
      return;
    }
    setOtherReferrer(true);
  };

  const handleOtherTextChange = (e: any) => {
    formik.handleChange(e);
  };

  const handleDateChange = (fieldName: string, date: dayjs.Dayjs | null) => {
    if (date) {
      const formattedDate = date.format("MM-DD-YYYY");
      formik.setFieldValue(fieldName, formattedDate);
    }
  };

  const handleCancel = () => {
    navigate(routes.registrationFormList);
  };

  useEffect(() => {
    getAreas()
      .then(setKarachiAreas)
      .catch(() => setKarachiAreas([]));
  }, []);

  useEffect(() => {
    if (formik.values.system) {
      fetchClasses(formik.values.system);
      setRegistrationType(null);
      formik.setFieldValue("appliedClass", null);
    }
  }, [formik.values.system]);

  useEffect(() => {
    if (formik.values.appliedClass && formik.values.session) {
      const selectedClass = allClass.find(
        (classOption) => classOption._id === formik.values.appliedClass
      );
      setRegistrationType(selectedClass?.registrationType);

      if (selectedClass?.registrationType === "interview+test") {
        fetchTestGroups(formik.values.appliedClass);
      }

      if (
        selectedClass?.registrationType === "interview" &&
        formik.values.testGroup
      ) {
        formik.setFieldValue("testGroup", "");
      }
    }
  }, [formik.values.appliedClass, formik.values.session, allClass]);

  const loadData = async () => {
    try {
      const studentData = location.state;

      if (studentData?.system?._id) {
        await fetchClasses(studentData.system._id);
      }

      setFormData({
        ...FORM_INITIAL_VALUES,
        ...nullsToEmptyStrings(studentData),
        system: studentData?.system?._id || "",
        appliedClass: studentData?.appliedClass?._id || "",
        testGroup: studentData?.testGroup?._id || "",
        session: studentData?.session?._id || "",
        branch: studentData?.branch?._id || studentData?.branch || DEFAULT_BRANCH_ID,
        noOfSiblings: studentData?.noOfSiblings ?? 0,
      });

      formik.setFieldValue(
        "appliedClass",
        studentData?.appliedClass?._id || ""
      );
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load student data");
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadData();
    }
  }, [isEdit]);

  const handleCapture = (imageSrc: string) => {
    if (currentCameraField) {
      fetch(imageSrc)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `${currentCameraField}.jpg`, {
            type: "image/jpeg",
          });
          formik.setFieldValue(currentCameraField, file);
        });
    }
  };
  return (
    <>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Edit" : "Add Registration"} Form
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <span>Registration</span>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "New"} Registration
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={formik.handleSubmit}>
                {/* Personal Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Personal Information</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {formik.values.studentPicture ? (
                              <img
                                src={
                                  typeof formik.values.studentPicture ===
                                  "string"
                                    ? formik.values.studentPicture
                                    : URL.createObjectURL(
                                        formik.values.studentPicture
                                      )
                                }
                                alt="Student"
                                style={{ width: "100%", height: "100%" }}
                              />
                            ) : (
                              <i className="ti ti-photo-plus fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                              <div className="drag-upload-btn mb-0">
                                <i className="ti ti-upload me-1"></i> Upload
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="form-control image-sign"
                                  name="studentPicture"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      formik.setFieldValue(
                                        "studentPicture",
                                        e.target.files[0]
                                      );
                                    }
                                  }}
                                />
                              </div>
                              <div className="drag-upload-btn mb-0">
                                <i className="ti ti-camera me-1"></i> Take Photo
                                <button
                                  type="button"
                                  className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                  onClick={() => {
                                    setCurrentCameraField("studentPicture");
                                    setShowCamera(true);
                                  }}
                                />
                              </div>
                              {formik.values.studentPicture && (
                                <div
                                  className="btn btn-primary mt-2"
                                  onClick={() => {
                                    formik.setFieldValue(
                                      "studentPicture",
                                      null
                                    );
                                  }}
                                >
                                  {location.state?.studentPicture
                                    ? "Restore"
                                    : "Remove"}
                                </div>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload image size 4MB, Format JPG, PNG, SVG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Session</label>
                          <CommonSelect
                            options={sessions}
                            value={sessions.find(
                              (option) => option._id === formik.values.session
                            )}
                            onChange={(selectedOption) => {
                              formik.setFieldValue(
                                "session",
                                selectedOption?._id || ""
                              );
                            }}
                            getOptionValue={(item) => item._id}
                            getOptionLabel={(item) => item.title}
                          />
                          {formik.touched.session && formik.errors.session ? (
                            <div className="text-danger">
                              {formik.errors.session}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">First Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.firstName &&
                              formik.errors.firstName
                                ? "is-invalid"
                                : ""
                            }`}
                            name="firstName"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.firstName &&
                          formik.errors.firstName ? (
                            <div className="text-danger">
                              {formik.errors.firstName}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              formik.touched.lastName && formik.errors.lastName
                                ? "is-invalid"
                                : ""
                            }`}
                            name="lastName"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.lastName && formik.errors.lastName ? (
                            <div className="text-danger">
                              {formik.errors.lastName}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">System</label>
                          <CommonSelect
                            className="select"
                            options={campusSystems}
                            getOptionValue={(item) => item._id}
                            getOptionLabel={(item) => item.name}
                            value={campusSystems.find(
                              (system) => system._id === formik.values.system
                            )}
                            onChange={(selectedOption) => {
                              formik.setFieldValue(
                                "system",
                                selectedOption?._id || ""
                              );
                            }}
                            isDisabled={loading}
                          />

                          {formik.touched.system && formik.errors.system ? (
                            <div className="text-danger">
                              {formik.errors.system}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Class/Grade</label>
                          {loading ? (
                            <div>Loading classes...</div>
                          ) : (
                            <>
                              <CommonSelect
                                className="select"
                                options={allClass}
                                getOptionValue={(item) => item._id}
                                getOptionLabel={(item) => item.name}
                                value={allClass.find(
                                  (selectedClass) =>
                                    selectedClass._id ===
                                    formik.values.appliedClass
                                )}
                                onChange={(selectedOption) => {
                                  formik.setFieldValue(
                                    "appliedClass",
                                    selectedOption?._id || ""
                                  );
                                }}
                                isDisabled={loading || !formik.values.system}
                              />
                            </>
                          )}
                          {formik.touched.appliedClass &&
                          formik.errors.appliedClass ? (
                            <div className="text-danger">
                              {formik.errors.appliedClass}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Gender</label>
                          <CommonSelect
                            className="select"
                            options={gender}
                            value={
                              gender.find(
                                (option) =>
                                  option.value === formik.values.gender
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              formik.setFieldValue(
                                "gender",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.gender && formik.errors.gender ? (
                            <div className="text-danger">
                              {formik.errors.gender}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Date of Birth</label>
                          <div className="input-icon position-relative">
                            <DatePicker
                              className={`form-control datetimepicker ${
                                formik.touched.dateOfBirth &&
                                formik.errors.dateOfBirth
                                  ? "is-invalid"
                                  : ""
                              }`}
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              value={
                                formik.values.dateOfBirth
                                  ? dayjs(formik.values.dateOfBirth)
                                  : null
                              }
                              onChange={(date) =>
                                handleDateChange("dateOfBirth", date)
                              }
                              maxDate={dayjs()}
                              disabledDate={(current) =>
                                current && current > dayjs().endOf("day")
                              }
                              showToday={false}
                              placeholder="Select Date"
                            />
                            <span className="input-icon-addon">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                          {formik.touched.dateOfBirth &&
                          formik.errors.dateOfBirth ? (
                            <div className="text-danger">
                              {formik.errors.dateOfBirth}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Blood Group</label>
                          <CommonSelect
                            className="select"
                            options={bloodGroup}
                            value={
                              bloodGroup.find(
                                (option) =>
                                  option.value === formik.values.bloodGroup
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              formik.setFieldValue(
                                "bloodGroup",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.bloodGroup &&
                          formik.errors.bloodGroup ? (
                            <div className="text-danger">
                              {formik.errors.bloodGroup}
                            </div>
                          ) : null}
                        </div>
                      </div> */}
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Category</label>
                          <CommonSelect
                            className="select"
                            options={cast}
                            value={
                              cast.find(
                                (option) =>
                                  option.value === formik.values.category
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              formik.setFieldValue(
                                "category",
                                selectedOption?.value
                              ) || ""
                            }
                          />
                          {formik.touched.category && formik.errors.category ? (
                            <div className="text-danger">
                              {formik.errors.category}
                            </div>
                          ) : null}
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>

                {/* Parents & Guardian Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-user-shield fs-16" />
                      </span>
                      <h4 className="text-dark">
                        Parents &amp; Guardian Information
                      </h4>
                    </div>
                  </div>
                  <div className="card-body pb-0">
                    <div className="border-bottom mb-3">
                      <h5 className="mb-3">Father's Info</h5>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                              {formik.values.fatherPicture ? (
                                <img
                                  src={
                                    typeof formik.values.fatherPicture ===
                                    "string"
                                      ? formik.values.fatherPicture
                                      : URL.createObjectURL(
                                          formik.values.fatherPicture
                                        )
                                  }
                                  alt="Student"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              ) : (
                                <i className="ti ti-photo-plus fs-16" />
                              )}
                            </div>
                            <div className="profile-upload">
                              <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-upload me-1"></i> Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control image-sign"
                                    name="fatherPicture"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        formik.setFieldValue(
                                          "fatherPicture",
                                          e.target.files[0]
                                        );
                                      }
                                    }}
                                  />
                                </div>
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-camera me-1"></i> Take
                                  Photo
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                    onClick={() => {
                                      setCurrentCameraField("fatherPicture");
                                      setShowCamera(true);
                                    }}
                                  />
                                </div>
                                {formik.values.fatherPicture && (
                                  <div
                                    className="btn btn-primary mt-2"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        "fatherPicture",
                                        null
                                      );
                                    }}
                                  >
                                    {location.state?.studentPicture
                                      ? "Restore"
                                      : "Remove"}
                                  </div>
                                )}
                              </div>
                              <p className="fs-12">
                                Upload image size 4MB, Format JPG, PNG, SVG
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Father Name</label>
                            <input
                              type="text"
                              className={`form-control ${
                                formik.touched.fatherName &&
                                formik.errors.fatherName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="fatherName"
                              value={formik.values.fatherName}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.fatherName &&
                            formik.errors.fatherName ? (
                              <div className="text-danger">
                                {formik.errors.fatherName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input
                              type="email"
                              className={`form-control ${
                                formik.touched.fatherEmail && formik.errors.fatherEmail
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="fatherEmail"
                              value={formik.values.fatherEmail}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.fatherEmail &&
                            formik.errors.fatherEmail ? (
                              <div className="text-danger">
                                {formik.errors.fatherEmail}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <PhoneInput
                              name="fatherPhoneNumber"
                              value={formik.values.fatherPhoneNumber}
                              onChange={(value: string) =>
                                formik.setFieldValue("fatherPhoneNumber", value)
                              }
                              className={
                                formik.touched.fatherPhoneNumber &&
                                formik.errors.fatherPhoneNumber
                                  ? "is-invalid"
                                  : ""
                              }
                            />

                            {formik.touched.fatherPhoneNumber &&
                            formik.errors.fatherPhoneNumber ? (
                              <div className="text-danger">
                                {formik.errors.fatherPhoneNumber}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-bottom mb-3">
                      <h5 className="mb-3">Mother's Info</h5>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                              {formik.values.motherPicture ? (
                                <img
                                  src={
                                    typeof formik.values.motherPicture ===
                                    "string"
                                      ? formik.values.motherPicture
                                      : URL.createObjectURL(
                                          formik.values.motherPicture
                                        )
                                  }
                                  alt="Student"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              ) : (
                                <i className="ti ti-photo-plus fs-16" />
                              )}
                            </div>
                            <div className="profile-upload">
                              <div className="profile-uploader d-flex align-items-center flex-wrap gap-1 mb-3">
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-upload me-1"></i> Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control image-sign"
                                    name="motherPicture"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        formik.setFieldValue(
                                          "motherPicture",
                                          e.target.files[0]
                                        );
                                      }
                                    }}
                                  />
                                </div>
                                <div className="drag-upload-btn mb-0">
                                  <i className="ti ti-camera me-1"></i> Take
                                  Photo
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                                    onClick={() => {
                                      setCurrentCameraField("motherPicture");
                                      setShowCamera(true);
                                    }}
                                  />
                                </div>
                                {formik.values.motherPicture && (
                                  <div
                                    className="btn btn-primary mt-2"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        "motherPicture",
                                        null
                                      );
                                    }}
                                  >
                                    {location.state?.studentPicture
                                      ? "Restore"
                                      : "Remove"}
                                  </div>
                                )}
                              </div>
                              <p className="fs-12">
                                Upload image size 4MB, Format JPG, PNG, SVG
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Mother Name</label>
                            <input
                              type="text"
                              className={`form-control ${
                                formik.touched.motherName &&
                                formik.errors.motherName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              name="motherName"
                              value={formik.values.motherName}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.motherName &&
                            formik.errors.motherName ? (
                              <div className="text-danger">
                                {formik.errors.motherName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-map fs-16" />
                      </span>
                      <h4 className="text-dark">Current Address</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Area</label>
                          <CommonSelect
                            className="select"
                            options={karachiAreas}
                            value={
                              karachiAreas.find(
                                (option) => option.value === formik.values.area
                              ) || null
                            }
                            isSearchable={true}
                            onChange={(selectedOption) =>
                              formik.setFieldValue(
                                "area",
                                selectedOption?.value || ""
                              )
                            }
                          />
                          {formik.touched.area && formik.errors.area ? (
                            <div className="text-danger">
                              {formik.errors.area}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* How did you hear about us */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-speakerphone fs-16" />
                      </span>
                      <h4 className="text-dark">How did you hear about us?</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Source</label>
                          <select
                            className="form-select"
                            onChange={handleOptionChange}
                            name="referrer"
                            value={formik.values.referrer}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select an option</option>
                            <option value="Social Media">Social Media</option>
                            <option value="Friend/Family">Friend/Family</option>
                            <option value="Google Search">Google Search</option>
                            <option value="Advertisement">Advertisement</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      {otherReferrer && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Please specify</label>
                            <textarea
                              className="form-control"
                              rows={1}
                              value={formik.values.otherReferrer}
                              onChange={handleOtherTextChange}
                              placeholder="Enter details"
                              name="otherReferrer"
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Interview & Test Schedule */}
                {registrationType && (
                  <div className="card">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-calendar-event fs-16" />
                        </span>
                        <h4 className="text-dark">
                          {registrationType === "interview"
                            ? "Interview"
                            : "Test"}{" "}
                          Schedule
                        </h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      {/* Show interview section if registrationType includes interview */}
                      {registrationType === "interview" ? (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Interview Date
                              </label>
                              <div className="input-icon position-relative">
                                <DatePicker
                                  className={`form-control datetimepicker ${
                                    formik.touched.interviewDate &&
                                    formik.errors.interviewDate
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  format={{
                                    format: "DD-MM-YYYY",
                                    type: "mask",
                                  }}
                                  value={
                                    formik.values.interviewDate
                                      ? dayjs(formik.values.interviewDate)
                                      : null
                                  }
                                  onChange={(date) =>
                                    handleDateChange("interviewDate", date)
                                  }
                                  minDate={dayjs()}
                                  placeholder="Select Date"
                                />
                                <span className="input-icon-addon">
                                  <i className="ti ti-calendar" />
                                </span>
                              </div>
                              {formik.touched.interviewDate &&
                              formik.errors.interviewDate ? (
                                <div className="text-danger small mt-1">
                                  {formik.errors.interviewDate}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Interview Time
                              </label>
                              <div className="input-icon position-relative">
                                <TimePicker
                                  className={`form-control datetimepicker ${
                                    formik.touched.interviewTime &&
                                    formik.errors.interviewTime
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  format="hh:mm A"
                                  use12Hours
                                  value={
                                    formik.values.interviewTime
                                      ? dayjs(
                                          formik.values.interviewTime,
                                          "HH:mm"
                                        )
                                      : null
                                  }
                                  onChange={(time) => {
                                    const formattedTime = time
                                      ? time.format("HH:mm")
                                      : "";
                                    formik.setFieldValue(
                                      "interviewTime",
                                      formattedTime
                                    );
                                  }}
                                  onBlur={formik.handleBlur}
                                  name="interviewTime"
                                  placeholder="Select Time"
                                  allowClear={false}
                                />
                                <span className="input-icon-addon">
                                  <i className="ti ti-clock" />
                                </span>
                              </div>
                              {formik.touched.interviewTime &&
                              formik.errors.interviewTime ? (
                                <div className="text-danger small mt-1">
                                  {formik.errors.interviewTime}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : registrationType === "interview+test" ? (
                        <div className="row mt-3 pb-3">
                          <div className="col-md-6">
                            <label className="form-label">Test Groups</label>
                            <CommonSelect
                              className="select"
                              options={testGroups}
                              value={testGroups.find(
                                (option) =>
                                  option?._id === formik.values.testGroup
                              )}
                              onChange={(selectedOption) =>
                                formik.setFieldValue(
                                  "testGroup",
                                  selectedOption?._id || ""
                                )
                              }
                              getOptionValue={(item) => item._id}
                              getOptionLabel={(item) => item.name}
                            />
                            {formik.touched.testGroup &&
                            formik.errors.testGroup ? (
                              <div className="text-danger small mt-1">
                                {formik.errors.testGroup}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      formik.isSubmitting ||
                      (isEdit
                        ? !user.permissions?.includes("registration.edit") &&
                          !!user.permissions?.length
                        : !user.permissions?.includes("registration.create") &&
                          !!user.permissions?.length)
                    }
                  >
                    {formik.isSubmitting
                      ? "Submitting..."
                      : isEdit
                      ? "Confirm"
                      : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showCamera && (
        <CameraModal
          onCapture={handleCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </>
  );
};

export default AddNewRegistration;
